import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { toggleSpinner } from 'apps/auth/actions/appAction';
import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { FlagIcon } from 'apps/auth/components/common/FlagIcon/FlagIcon';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import PopupPortal from 'apps/auth/components/common/Popup/PopupPortal';
import { Text } from 'apps/auth/components/common/Text/Text';
import UsaHighRiskCountryOptInModal from 'apps/auth/components/send/optIn/UsaHighRiskCountryOptInModal';
import TextPopup from 'apps/auth/components/sharedComponents/popups/TextPopup';
import { getRecipientTypeParam } from 'apps/auth/components/utils/AmplitudeUtils';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { RECEIVING_COUNTRY_SELECTED, RESTRICTED_COUNTRY_BOTTOM_SHEET_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { FROM, PAYMENT_FLOW, PAYOUT_COUNTRY, PAYOUT_CURRENCY, RECIPIENT_TYPE, SUGGESTED } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { NEW, PROMO } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { TEXT_POPUP } from 'apps/auth/constants/ComponentNames';
import { apiFetchData } from 'apps/auth/helpers/api';
import { getHighlightedText, resolveArguments, toLowerCase } from 'apps/auth/helpers/utils';
import { useExtra } from 'apps/auth/hooks/selectors';

import { CountryItemPanel } from './components/CountryItemPanel/CountryItemPanel';

const CurrencyAbbr = styled.span`
    &:before {
        content: '·';
        display: inline-block;
        margin: 0 4px;
    }

    &:first-child:before {
        display: none;
    }
`;

const RecentCountryItem = styled(Link)`
    padding: 16px 8px;
    width: 33.33%;
    min-width: 33.33%;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    text-align: center;
    box-sizing: border-box;

    &:hover {
        background: ${(props) => props.theme.grey_8_lc};
    }
`;

const FlagWrapper = styled.div`
    margin-bottom: 8px;
`;

const CountryItemPureComponent = (props) => {
    const [popup, setPopup] = useState('');
    const popupRef = useRef(null);
    const popupFullwidthRef = useRef(null);
    const modalRef = useRef(null);
    const { recipientList, pocket: isGA, searchCountryName, listOfLacCountries } = useExtra();
    const country = props.country;
    const userCountry = props.userCountry;
    const isRecipientList = Array.isArray(recipientList) && recipientList.length > 0;
    const amplitudeFromButton = isRecipientList ? 'recipient_list' : ' transfer_button';

    useEffect(() => {
        if (searchCountryName === country.name) {
            click({}, country);
            props.updateExtraByName('searchCountryName', null);
        }
    }, []);

    const getHref = (countryCode) => {
        return `${props.sendUrIPrefix}/${toLowerCase(countryCode)}/delivery${props.sendUriPostfix || ''}`;
    };

    const amplitudeEvent = (country) => {
        if (!isGA) {
            amplitude.getInstance().logEvent(RECEIVING_COUNTRY_SELECTED, {
                [PAYOUT_COUNTRY]: country?.nameEn,
                [SUGGESTED]: listOfLacCountries?.includes(country.code) || !!props.suggestedCountry,
                [PAYOUT_CURRENCY]: country.currencies[0]?.code,
                [FROM]: listOfLacCountries ? PROMO : amplitudeFromButton,
                [PAYMENT_FLOW]: NEW,
                [RECIPIENT_TYPE]: getRecipientTypeParam(props.recipientType),
            });
        }
    };
    const getPopupComponent = (country) => {
        const disabledReasonText = country?.disabledReason;
        const args = [userCountry?.notificationNameFrom, country?.notificationNameTo];
        if (popup === TEXT_POPUP) {
            return (
                <TextPopup
                    text={disabledReasonText ? disabledReasonText : resolveArguments(props.notificationText, args)}
                    buttonText={props.gotIt}
                    popupRef={popupRef}
                />
            );
        }
    };

    const openDisabledCountryPopup = (country) => {
        amplitude.getInstance().logEvent(RESTRICTED_COUNTRY_BOTTOM_SHEET_SHOWN, {
            [PAYOUT_COUNTRY]: country?.nameEn,
        });
        setPopup(TEXT_POPUP);
        popupRef.current?.open(country);
    };
    const checkDisabledCountry = (country) => {
        props.toggleSpinner(true);
        apiFetchData(getHref(country.code))
            .then((json) => {
                const updatedCountry = json?.extra?.toCountry || country;
                if (updatedCountry?.migrationOnly) {
                    if (json?.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    popupFullwidthRef.current?.open(
                        <UsaHighRiskCountryOptInModal
                            modalRef={modalRef}
                            popupFullwidthRef={popupFullwidthRef}
                            country={updatedCountry}
                            title={updatedCountry?.migrationOnlyTitle}
                            subtitle={updatedCountry?.migrationOnlySubTitle}
                        />,
                    );
                    return;
                }
                openDisabledCountryPopup(updatedCountry);
            })
            .catch(() => {
                openDisabledCountryPopup(country);
            })
            .finally(() => {
                props.toggleSpinner(false);
            });
    };

    const click = (e, country) => {
        amplitudeEvent(country);
        props.sendBrazeEvent?.(country);
        if (country.disabled) {
            e.preventDefault?.();
            if (props.sendUrIPrefix) {
                checkDisabledCountry(country);
            } else {
                openDisabledCountryPopup(country);
            }
        } else if (props.onCountryItemClick) {
            e.preventDefault?.();
            props.onCountryItemClick(e, country, getHref, props.ccyField);
        }
        if (props.sendUrIPrefix) {
            sessionStorage.setItem('allow_payout_promo_info', 'true');
        }
    };

    const getItemHref = () => {
        let currencyCode = country.currencies[0]?.code;
        return getHref(country.code, currencyCode);
    };

    return (
        <>
            {props.isLongPanel ? (
                <CountryItemPanel
                    to={props.sendUrIPrefix ? getItemHref() : null}
                    onClick={(e) => click(e, country)}
                    flagIconName={country.code}
                    countryName={country.name}
                    countryCurrency={country.currencies?.map((ccy) => (
                        <CurrencyAbbr key={`cnrCode_${country.code}_ccy_${ccy.code}`}>
                            {props.query ? getHighlightedText(props.query, ccy.code, 'text', { basicColor: 'secondary' }) : ccy.code}
                        </CurrencyAbbr>
                    ))}
                    showCurrency={props.showCurrency}
                    query={props.query}
                    isSelectedCountry={props.getSelectedCountry?.(country)}
                    isDisabledPanel={country.disabled}
                />
            ) : (
                <RecentCountryItem to={getItemHref()} onClick={(e) => click(e, country)}>
                    <FlagWrapper>
                        <FlagIcon name={country.code} isDisabled={country.disabled} size="small" />
                    </FlagWrapper>
                    <Text size="secondary" color={country.disabled ? 'secondary' : 'primary'}>
                        {country.name}
                    </Text>
                </RecentCountryItem>
            )}
            <PopupNew ref={popupRef} getPopupComponent={getPopupComponent} as={PopupPortal} />
            <PopupNew ref={popupFullwidthRef} fullwidthPopup />
            <ModalNew ref={modalRef} />
        </>
    );
};

const CountryItem = connect(null, { toggleSpinner, updateExtraByName, updatePageMessages })(CountryItemPureComponent);
export default CountryItem;
